import { Button, DdsDrawCell, BreakdownCell } from "@common"; // BreakdownCell added for hover tooltips on financial columns
import { Link } from "react-router-dom";
import { formatMoney } from "./format";
import {
  hasFeeConfigured,
  isApprovalApproved,
  computeTotalDeductions,
  TotalFeeHeader,
} from "./associateFee";
import {
  LINK_CLASSES,
  TYPE_PILL_COLORS,
  DDS_DRAW_COLORS,
  PROFIT_COLORS,
  OTHER_PROFIT_COLORS,
  TOTAL_FEE_COLORS,
  DEDUCTION_COLORS,
  FEE_PERCENT_COLORS,
} from "./columnColors";
// NOTE: Data here comes from MonthlyCompensationSerializer which returns financial
// fields as nested objects { total, breakdown } — always use r.field?.total for
// the value and r.field?.breakdown for the slot array (Dentrix/Tracker1/Tracker2).

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * DDS Draw is calculated by the backend. This module only consumes the
 * `breakdown` dict the API attaches to every row. See
 * `apps/monthly_compensation/calculations.py` for the canonical formula.
 */
export const calculateDdsDraw = (row) => toNum(row?.breakdown?.dds_draw);

const percent = (n) => {
  if (n === null || n === undefined || n === "") return "-";
  const v = Number(n);
  if (Number.isNaN(v)) return "-";
  return `${v.toFixed(2)}%`;
};

const textOrDash = (v) => (v != null && String(v).trim() !== "" ? v : "-");

const hasFee = hasFeeConfigured;

/**
 * Columns for the Journal Entry table. Same shape as payrollColumns but
 * WITHOUT the PDF, EMAIL, and EMAILED columns.
 */
export const journalEntryColumns = (
  onUpdateClick,
  { onApprove, onPractitionerClick, deductionsExpanded = false, onToggleDeductions } = {},
) => [
  {
    key: "clinic",
    header: "CLINIC",
    width: 100,
    render: (r) => {
      const code = r.clinic;
      if (!code) return textOrDash(code);
      const monthParam = /^\d{4}-\d{2}/.test(String(r.month || ""))
        ? String(r.month).slice(0, 7)
        : "";
      const to = `/report/${encodeURIComponent(code)}?tab=monthly${
        monthParam ? `&month=${monthParam}` : ""
      }`;
      return (
        <Link to={to} title={`Open ${code} monthly reconciliation`} className={LINK_CLASSES}>
          {code}
        </Link>
      );
    },
  },
  {
    key: "practitioner_name",
    header: "PRACTITIONER",
    width: 140,
    render: (r) => {
      const name = textOrDash(r.practitioner_name);
      if (!onPractitionerClick || name === "-" || !r.practitioner_id) return name;
      return (
        <button
          type="button"
          onClick={() => onPractitionerClick(r)}
          title="View practitioner profile"
          className={LINK_CLASSES}
        >
          {name}
        </button>
      );
    },
  },
  {
    key: "practitioner_type",
    header: "TYPE",
    width: 70,
    render: (r) => {
      const t = (r.practitioner_type || "").toString().toUpperCase();
      if (!t) return <span className="text-palette-gray-placeholder text-xs">-</span>;
      const color = TYPE_PILL_COLORS[t] || TYPE_PILL_COLORS.default;
      return (
        <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${color}`}>
          {t}
        </span>
      );
    },
  },
  {
    key: "dds_draw",
    header: "DDS DRAW",
    width: 120,
    headerClassName: DDS_DRAW_COLORS.header,
    cellClassName: DDS_DRAW_COLORS.cell,
    render: (r) => {
      const isHyg = String(r.practitioner_type || "").toUpperCase() === "HYG";
      if (!hasFee(r)) {
        if (isHyg) {
          return (
            <span className="text-palette-gray-placeholder text-xs italic">
              Not applicable (HYG)
            </span>
          );
        }
        return <span className="text-palette-gray-placeholder text-xs italic">Set fee %</span>;
      }
      return <DdsDrawCell row={r} compact />;
    },
  },
  {
    key: "production",
    header: "PRODUCTION",
    width: 100,
    headerClassName: PROFIT_COLORS.header,
    cellClassName: PROFIT_COLORS.cell,
    render: (r) =>
      toNum(r.production?.total) === 0 ? (
        <span className="text-palette-sky-heading/70 text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.production?.total}
          breakdown={r.production?.breakdown}
          label="Production"
        />
      ),
  },
  {
    key: "collection",
    header: "COLLECTION",
    width: 100,
    headerClassName: PROFIT_COLORS.header,
    cellClassName: PROFIT_COLORS.cell,
    render: (r) =>
      toNum(r.collection?.total) === 0 ? (
        <span className="text-palette-sky-heading/70 text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.collection?.total}
          breakdown={r.collection?.breakdown}
          label="Collection"
        />
      ),
  },
  {
    key: "other_profit",
    header: "OTHER PROFIT",
    width: 110,
    headerClassName: OTHER_PROFIT_COLORS.header,
    cellClassName: OTHER_PROFIT_COLORS.cell,
    render: (r) => {
      const tiers = Array.isArray(r.other_profit) ? r.other_profit : [];
      if (tiers.length === 0)
        return <span className="text-palette-gray-placeholder text-xs">N/A</span>;
      const total = tiers.reduce((sum, t) => sum + toNum(t?.profit), 0);
      const title = tiers
        .map((t) => {
          const pct = Number(t?.percent) || 0;
          const input = toNum(t?.input_value);
          const profit = toNum(t?.profit);
          return `${t?.label || "?"} (${pct}%): ${formatMoney(input)} → ${formatMoney(profit)}`;
        })
        .join("\n");
      return (
        <span className="text-palette-sky-primary cursor-help" title={title}>
          {formatMoney(total)}
        </span>
      );
    },
  },
  {
    key: "total_fee",
    header: <TotalFeeHeader expanded={deductionsExpanded} onToggle={onToggleDeductions} />,
    width: 110,
    align: "center",
    headerClassName: TOTAL_FEE_COLORS.header,
    cellClassName: TOTAL_FEE_COLORS.cell,
    render: (r) => {
      const total = computeTotalDeductions(r);
      if (total === 0) return <span className="text-palette-gray-placeholder text-xs">N/A</span>;
      return <span className="font-semibold text-palette-rose-primary">{formatMoney(total)}</span>;
    },
  },
  {
    key: "lab_cost",
    header: "LAB COSTS",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.lab_cost?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.lab_cost?.total}
          breakdown={r.lab_cost?.breakdown}
          label="Lab Costs"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "material_cost",
    header: "MATERIAL",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.material_cost?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.material_cost?.total}
          breakdown={r.material_cost?.breakdown}
          label="Material"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "invisalign_cost",
    header: "INVISALIGN",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.invisalign_cost?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.invisalign_cost?.total}
          breakdown={r.invisalign_cost?.breakdown}
          label="Invisalign"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "anesthesia_fee",
    header: "ANESTHESIA",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.anesthesia_fee?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.anesthesia_fee?.total}
          breakdown={r.anesthesia_fee?.breakdown}
          label="Anesthesia"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "anesthesiologist_fee",
    header: "ANESTHESIOLOGIST",
    width: 110,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.anesthesiologist_fee?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.anesthesiologist_fee?.total}
          breakdown={r.anesthesiologist_fee?.breakdown}
          label="Anesthesiologist"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "facility_fee",
    header: "FACILITY",
    width: 80,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.facility_fee?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.facility_fee?.total}
          breakdown={r.facility_fee?.breakdown}
          label="Facility"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "botox",
    header: "BOTOX",
    width: 70,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.botox?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.botox?.total}
          breakdown={r.botox?.breakdown}
          label="Botox"
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "production_adjustment",
    header: "PRODUCTION ADJ.",
    width: 70,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.production_adjustment?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.production_adjustment?.total}
          breakdown={r.production_adjustment?.breakdown}
          label="Production Adj."
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "collection_adjustment",
    header: "COLLECTION ADJ.",
    width: 70,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.collection_adjustment?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.collection_adjustment?.total}
          breakdown={r.collection_adjustment?.breakdown}
          label="Collection Adj."
          className="text-palette-slate-primary"
        />
      ),
  },
  {
    key: "other_deduction",
    header: "OTHER DEDUCTION",
    width: 110,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) => {
      const tiers = Array.isArray(r.other_deduction) ? r.other_deduction : [];
      if (tiers.length === 0)
        return <span className="text-palette-gray-placeholder text-xs">N/A</span>;
      const amountOf = (t) => {
        const explicit = Number(t?.amount);
        if (Number.isFinite(explicit) && explicit !== 0) return explicit;
        if (t?.hour_rate !== undefined || t?.hours !== undefined) {
          return (Number(t?.hour_rate) || 0) * (Number(t?.hours) || 0);
        }
        return Number(t?.profit) || 0;
      };
      const total = tiers.reduce((sum, t) => sum + (Number(amountOf(t)) || 0), 0);
      const title = tiers
        .map((t) => {
          const amt = amountOf(t);
          if (t?.hour_rate !== undefined || t?.hours !== undefined) {
            const hr = Number(t?.hour_rate) || 0;
            const hrs = Number(t?.hours) || 0;
            return `${t?.label || "?"} (${hrs}h × ${formatMoney(hr)}): ${formatMoney(amt)}`;
          }
          const pct = Number(t?.percent) || 0;
          const input = toNum(t?.input_value);
          return `${t?.label || "?"} (${pct}%): ${formatMoney(input)} → ${formatMoney(amt)}`;
        })
        .join("\n");
      return (
        <span className="text-palette-slate-primary cursor-help" title={title}>
          {formatMoney(total)}
        </span>
      );
    },
  },
  {
    key: "insurance",
    header: "INSURANCE",
    width: 90,
    headerClassName: DEDUCTION_COLORS.header,
    cellClassName: DEDUCTION_COLORS.cell,
    render: (r) =>
      toNum(r.insurance) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <span className="text-palette-slate-primary">{formatMoney(r.insurance)}</span>
      ),
  },
  {
    key: "hours_worked",
    header: "HOURS",
    width: 70,
    render: (r) =>
      toNum(r.hours_worked?.total) === 0 ? (
        <span className="text-palette-gray-placeholder text-xs">N/A</span>
      ) : (
        <BreakdownCell
          value={r.hours_worked?.total}
          breakdown={r.hours_worked?.breakdown}
          label="Hours"
          formatter={(v) => Number(v).toFixed(2)}
        />
      ),
  },
  {
    key: "associate_fee_percentage",
    header: "ASSOCIATE FEE %",
    width: 80,
    headerClassName: FEE_PERCENT_COLORS.header,
    cellClassName: FEE_PERCENT_COLORS.cell,
    render: (r) => {
      if (!hasFee(r) && onUpdateClick) {
        return (
          <Button variant="gray" size="xs" onClick={() => onUpdateClick(r)}>
            Update
          </Button>
        );
      }
      return percent(r.associate_fee_percentage);
    },
  },
  {
    key: "approval",
    header: "APPROVAL BY ADMIN",
    width: 130,
    // Status-only: reflects admin (final) approval. No action button here.
    render: (r) =>
      isApprovalApproved(r.admin_approval) ? (
        <span className="text-palette-green-badge text-base" title="Admin approved">
          ✓
        </span>
      ) : (
        <span className="text-palette-red-indicator text-base" title="Not yet admin-approved">
          ✗
        </span>
      ),
  },
];
